import React, { Fragment, useState, useMemo, useEffect } from 'react'
import Widget from '@modul-connect/shared/components/atoms/widget'
import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ReferenceLine
} from 'recharts'
import { DateTime } from 'luxon'
import { useTranslation } from 'react-i18next'

const LogChartMultipleCurves = ({
  data,
  curves,
  unit,
  displayTimeInHours,
  intervalOptions,
  onIntervalChanged,
  selectedInterval,
  yAxisPadding,
  noYDecimals,
  oldPicker,
  setStartTime,
  setEndTime,
  startTime,
  endTime,
  graphHeight,
  colors,
  isLoading,
  fetchFailed
}) => {

  const {t} = useTranslation()

  const formattedData = useMemo(() => {
    if (!data || !Array.isArray(data)) return []

    const groupedMap = new Map()

    data.forEach(item => {

      const timeMs = DateTime.fromISO(item.timestamp).toMillis()
      if (!groupedMap.has(timeMs)) {
        groupedMap.set(timeMs, { timestamp: timeMs })
      }
      
      const entry = groupedMap.get(timeMs)

      entry[item.sensorId] = item.value
    })

    return Array.from(groupedMap.values()).sort((a, b) => a.timestamp - b.timestamp)
  }, [data])

  const thresholdColors = curves.length === 1 || curves.length > 1 && curves.every(c => c.highThreshold === curves[0].highThreshold && c.lowThreshold === curves[0].lowThreshold) 
    ? new Array(curves.length).fill('#000')
    : colors

  const [visibleCurves, setVisibleCurves] = useState(
    () => Object.fromEntries(curves.map(c => [c.sensorId, true]))
  )

  const toggleCurve = (dataKey) => {
    setVisibleCurves(prev => ({
      ...prev,
      [dataKey]: !prev[dataKey]
    }))
  }

  useEffect(() => {
    setVisibleCurves(() => Object.fromEntries(curves.map(c => [c.sensorId, true])))
  }, [data])

  const tickFormatter = displayTimeInHours
    ? (ms) => {
        const d = DateTime.fromMillis(ms)
        return `${String(d.hour).padStart(2, '0')}:${String(d.minute).padStart(2, '0')}`
      }
    : (ms) => {
        const d = DateTime.fromMillis(ms)
        return `${d.year}-${String(d.month).padStart(2, '0')}-${String(d.day).padStart(2, '0')}`
      }


  const xDomain = useMemo(() => {
    let minTime = null;
    let maxTime = null;

    Object.entries(visibleCurves).forEach(([sensorId, isVisible]) => {
      if (!isVisible) return;

      const validData = formattedData.filter(d => 
        d[sensorId] !== undefined && d[sensorId] !== null
      );

      if (validData.length > 0) {
        const start = validData[0].timestamp;
        const end = validData[validData.length - 1].timestamp;

        if (minTime === null || start < minTime) minTime = start;
        if (maxTime === null || end > maxTime) maxTime = end;
      }
    });

    if (minTime === null || maxTime === null) {
      return ['dataMin', 'dataMax'];
    }

    return [minTime, maxTime];
  }, [visibleCurves, formattedData]);
  
  return (
    <Fragment>
      <Widget
        oldPicker={oldPicker}
        setStartTime={setStartTime}
        setEndTime={setEndTime}
        startTime={startTime}
        endTime={endTime}
        show={true}
        intervalOptions={intervalOptions}
        onOptionSelected={onIntervalChanged}
        selectedInterval={selectedInterval}
        noTimePicker={true}
        children={[
          {
            name: 'right',
            body: (
              <div style={{ position: 'relative', width: '100%', height: graphHeight }}>
                {isLoading && (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(255, 255, 255, 0.6)',
                    zIndex: 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.2rem',
                    color: '#666'
                  }}>
                    <span>{t('climateSystem.tempHistory.loadingData')}</span>
                  </div>
                )}
                {fetchFailed && (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(255, 255, 255, 0.6)',
                    zIndex: 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.2rem',
                    color: '#666'
                  }}>
                    <span>{t('climateSystem.tempHistory.fetchFailed')}</span>
                  </div>
                )}
                <ResponsiveContainer width="99%" height={graphHeight}>
                  <LineChart data={formattedData} margin={{ top: 20 }}>
                    <CartesianGrid stroke="#ccc" strokeDasharray="3" />

                    <XAxis
                      dataKey="timestamp"
                      interval="preserveStartEnd"
                      type="number"
                      domain={xDomain} 
                      tickFormatter={tickFormatter}
                      allowDataOverflow={true}
                    />

                    <YAxis
                      type="number"
                      unit={unit}
                      padding={yAxisPadding}
                      allowDecimals={!noYDecimals}

                      domain={[
                        (dataMin) => {
                          if (!isFinite(dataMin)) return 0
                          const minThreshold = Math.min(...curves.map(c => c.lowThreshold))
                          if (minThreshold === null) return Math.floor(dataMin - 5)
                          return Math.floor(dataMin - 5) < minThreshold ? Math.floor(dataMin - 5) : minThreshold
                        },
                        (dataMax) => {
                          if (!isFinite(dataMax)) return 100
                          const maxThreshold = Math.max(...curves.map(c => c.highThreshold))
                          if (maxThreshold === null) return Math.ceil(dataMax + 5)
                          return Math.ceil(dataMax + 5) > maxThreshold ? Math.ceil(dataMax + 5) : maxThreshold
                        }
                      ]}
                    />

                    <Tooltip 
                      content={<CustomTooltip curves={curves} />}
                    />

                    <Legend
                      onClick={(e) => toggleCurve(e.dataKey)}
                      formatter={(value, entry) => {

                        const isVisible = visibleCurves[entry.dataKey];
                        return (
                          <span 
                            style={{ 
                              cursor: 'pointer', 
                              color: isVisible ? '#000' : '#ccc', 
                              textDecoration: isVisible ? 'none' : 'line-through'
                            }}
                          >
                            {value}
                          </span>
                        )
                      }}
                    />

                  {curves.map((curve, i) => {
                    const isHidden = !visibleCurves[curve.sensorId];
                    
                    const highVal = curve.highThreshold ?? curve.highTreshold;
                    const lowVal = curve.lowThreshold;

                    const startX = xDomain[0];
                    const endX = xDomain[1];

                    return (
                      <Fragment key={curve.sensorId}>

                        <Line
                          connectNulls={true}
                          dot={false}
                          hide={isHidden}
                          activeDot
                          type="monotone"
                          dataKey={curve.sensorId} 
                          name={curve.name}
                          stroke={colors[i]}
                          strokeWidth={2}
                        />

                        {!isHidden && highVal !== undefined && highVal !== null && (
                          <ReferenceLine
                            segment={[
                              { x: startX, y: highVal },
                              { x: endX, y: highVal }
                            ]}
                            stroke={thresholdColors[i]}
                            strokeDasharray="14 10"
                            strokeWidth={1.5}
                            ifOverflow="extendDomain"
                          />
                        )}

                        {!isHidden && lowVal !== undefined && lowVal !== null && (
                          <ReferenceLine
                            segment={[
                              { x: startX, y: lowVal },
                              { x: endX, y: lowVal }
                            ]}
                            stroke={thresholdColors[i]}
                            strokeDasharray="14 10"
                            strokeWidth={1.5}
                            ifOverflow="extendDomain"
                          />
                        )}
                      </Fragment>
                    )
                  })}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )
          }
        ]}
      />
    </Fragment>
  )
}

const CustomTooltip = ({ active, payload, label, curves }) => {
  if (!active || !payload || !payload.length) return null

  const dateLabel = DateTime.fromMillis(label).toFormat('yyyy-MM-dd HH:mm')

  return (
    <div style={{
      backgroundColor: '#fff',
      border: '1px solid #ccc',
      padding: '10px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      borderRadius: '4px',
      fontSize: '16px'
    }}>
      <p style={{ margin: '0 0 5px' }}>{dateLabel}</p>
      
      {payload.map((entry, index) => {

        const curve = curves.find(c => String(c.sensorId) === String(entry.dataKey))
        
        if (!curve) return null
        
        const high = curve.highThreshold ?? curve.highTreshold
        const low = curve.lowThreshold

        return (
          <div key={index} style={{ marginBottom: '5px', color: entry.color }}>
            <div>
              {curve.name}: {entry.value}
            </div>

            <div style={{ fontSize: '12px', opacity: 0.8, marginLeft: '5px' }}>
              {high !== undefined && high !== null && <span>Max: {high} </span>}
              {low !== undefined && low !== null && <span>Min: {low}</span>}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default LogChartMultipleCurves